/**
 * CyclePhaseCard — iter1092
 *
 * Отделен, ясен UI за текуща фаза на цикъла — сега първокласна секция
 * от профила (преди беше скрит зад overrides в AutoCycleSettings).
 *
 * Показва:
 *   • Автоматично изчислена фаза от LMP + cycle length (ако има данни).
 *   • Manual pill picker за 4-те фази.
 *   • Explainer какво отключва (Hormonal twin, DIL модификатор, BHI).
 *
 * Пази към backend: PUT /api/dil/cycle → `profile.reproductive.cycle_phase`
 * + `profile.cyclePhase` (backwards-compat).
 */
import React, { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { Sparkles, Save, RotateCcw, Bell, BellOff } from "lucide-react";
import { deriveCyclePhase } from "@/lib/profileCompleteness";
import { usePushNotifications } from "@/hooks/usePushNotifications";

const PHASES = [
  { key: "menstrual", labelBg: "Менструална", labelEn: "Menstrual", emoji: "🩸",
    hintBg: "Ден 1–4. По-ниска чувствителност към инсулин.", color: "rose" },
  { key: "follicular", labelBg: "Фоликуларна", labelEn: "Follicular", emoji: "🌱",
    hintBg: "Ден 5–13. Оптимална чувствителност, най-добра форма.", color: "emerald" },
  { key: "ovulatory", labelBg: "Овулаторна", labelEn: "Ovulatory", emoji: "☀️",
    hintBg: "Ден 14–16. Пик на естроген, енергията е висока.", color: "amber" },
  { key: "luteal", labelBg: "Лутеална", labelEn: "Luteal", emoji: "🌙",
    hintBg: "Ден 17+. По-строг контрол на въглехидратите (+10% DIL).", color: "violet" },
];

// iter1094 — Simple relative-time formatter за push history
function formatWhen(iso, bg) {
  if (!iso) return "";
  const dt = new Date(iso);
  const s = Math.round((Date.now() - dt.getTime()) / 1000);
  if (s < 60) return bg ? "преди < 1 мин" : "< 1 min ago";
  if (s < 3600) return bg ? `преди ${Math.round(s / 60)} мин` : `${Math.round(s / 60)} min ago`;
  if (s < 86400) return bg ? `преди ${Math.round(s / 3600)} ч` : `${Math.round(s / 3600)} h ago`;
  return bg ? `преди ${Math.round(s / 86400)} д` : `${Math.round(s / 86400)} d ago`;
}

export default function CyclePhaseCard() {
  const { API, user, refreshUser } = useAuth();
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const bg = language === "bg";
  const profile = user?.profile || {};
  const reproductive = profile?.reproductive || {};

  const savedPhase =
    reproductive.cycle_phase
    || profile.cyclePhase
    || null;
  const derived = useMemo(
    () => deriveCyclePhase(profile.lastPeriodDate, profile.avgCycleLength),
    [profile.lastPeriodDate, profile.avgCycleLength],
  );
  const [pick, setPick] = useState(savedPhase);
  const [saving, setSaving] = useState(false);
  // iter1093 — Live preview на импакта по фаза (DIL + Hormonal twin)
  const [preview, setPreview] = useState(null);
  // iter1093 — Push notifications toggle
  const push = usePushNotifications(API);
  // iter1094 — Push history timeline (last N nudges)
  const [pushHistory, setPushHistory] = useState(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/dil/cycle-phase-preview`);
        if (!cancelled) setPreview(data);
      } catch (_e) { /* silent — може профилът да е непопълнен */ }
    })();
    return () => { cancelled = true; };
  }, [API, savedPhase]);

  // iter1094 — Fetch push history when push tab is available.
  React.useEffect(() => {
    let cancelled = false;
    if (!push.subscribed) { setPushHistory(null); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/cycle-phase/push-history`);
        if (!cancelled) setPushHistory(data);
      } catch (_e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [API, push.subscribed]);

  // Показваме "current" — първо ръчното, после derived. Ако липсва и двете → null.
  const effective = pick || derived;
  const meta = PHASES.find((p) => p.key === effective);

  const save = async (phaseKey) => {
    setSaving(true);
    try {
      await axios.put(`${API}/dil/cycle`, { cyclePhase: phaseKey });
      setPick(phaseKey === "none" ? null : phaseKey);
      toast.success(bg ? "Фазата е записана" : "Cycle phase saved");
      if (refreshUser) await refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || (bg ? "Грешка при запис" : "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="cycle-phase-card"
      className={`rounded-2xl border p-4 ${
        isDark ? "bg-[hsl(222,47%,10%)] border-[hsl(223,47%,18%)]" : "bg-white border-slate-200"
      }`}
    >
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className={`w-4 h-4 ${isDark ? "text-fuchsia-300" : "text-fuchsia-600"}`} />
        <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
          {bg ? "Текуща фаза на цикъла" : "Current cycle phase"}
        </h3>
      </div>
      <p className={`text-xs mb-3 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
        {bg
          ? "Отключва Hormonal twin, персонализира DIL бюджета и Breast Intelligence."
          : "Unlocks Hormonal twin, personalises the DIL budget and Breast Intelligence."}
      </p>

      {/* Effective phase preview */}
      {meta ? (
        <div
          data-testid="cycle-phase-current"
          className={`flex items-center gap-2 rounded-xl px-3 py-2 mb-3 border ${
            isDark ? `bg-${meta.color}-500/10 border-${meta.color}-500/30 text-${meta.color}-200`
                   : `bg-${meta.color}-50 border-${meta.color}-200 text-${meta.color}-800`
          }`}
        >
          <span className="text-xl leading-none">{meta.emoji}</span>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold">
              {bg ? meta.labelBg : meta.labelEn}
              {pick && (
                <span className={`ml-2 text-[10px] font-normal opacity-70`}>
                  {bg ? "(ръчно)" : "(manual)"}
                </span>
              )}
              {!pick && derived && (
                <span className={`ml-2 text-[10px] font-normal opacity-70`}>
                  {bg ? "(автоматично)" : "(auto)"}
                </span>
              )}
            </div>
            <div className="text-[11px] opacity-80">{meta.hintBg}</div>
          </div>
        </div>
      ) : (
        <div
          className={`rounded-xl px-3 py-2 mb-3 text-xs ${
            isDark ? "bg-slate-800/50 text-slate-400" : "bg-slate-50 text-slate-600"
          }`}
          data-testid="cycle-phase-empty"
        >
          {bg
            ? "Няма записана фаза. Попълнете дата на последна менструация в „Auto-cycle“ или изберете ръчно ↓"
            : "No phase recorded. Enter last period date in Auto-cycle or pick manually ↓"}
        </div>
      )}

      {/* Manual picker */}
      <div className="grid grid-cols-2 gap-2">
        {PHASES.map((p) => {
          const active = pick === p.key || (!pick && derived === p.key);
          return (
            <button
              key={p.key}
              type="button"
              disabled={saving}
              onClick={() => save(p.key)}
              data-testid={`cycle-phase-pick-${p.key}`}
              className={`px-3 py-2 rounded-xl text-xs font-semibold border transition-all disabled:opacity-50 text-left ${
                active
                  ? (isDark ? `bg-${p.color}-500/20 border-${p.color}-500/50 text-${p.color}-200`
                            : `bg-${p.color}-100 border-${p.color}-400 text-${p.color}-800`)
                  : (isDark ? "bg-white/5 border-white/10 hover:bg-white/10 text-slate-300"
                            : "bg-white border-slate-200 hover:bg-slate-50 text-slate-700")
              }`}
            >
              <div className="flex items-center gap-1.5">
                <span className="text-base leading-none">{p.emoji}</span>
                <span>{bg ? p.labelBg : p.labelEn}</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Clear button */}
      {(pick || savedPhase) && (
        <button
          type="button"
          disabled={saving}
          onClick={() => save("none")}
          data-testid="cycle-phase-clear"
          className={`mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full ${
            isDark ? "bg-white/5 text-slate-400 hover:text-slate-200" : "bg-slate-100 text-slate-500 hover:text-slate-700"
          }`}
        >
          <RotateCcw className="w-3 h-3" /> {bg ? "Изчисти ръчното" : "Clear override"}
        </button>
      )}

      {/* iter1093 — Twin Impact Preview: сравни всяка фаза срещу baseline */}
      {preview?.phases?.length ? (
        <div
          className={`mt-4 pt-3 border-t ${isDark ? "border-white/10" : "border-slate-200"}`}
          data-testid="cycle-phase-impact-preview"
        >
          <div className={`text-[10px] uppercase font-bold tracking-wider mb-2 ${
            isDark ? "text-fuchsia-300" : "text-fuchsia-700"
          }`}>
            {bg ? "Импакт по фаза" : "Impact by phase"}
          </div>
          <div className="grid grid-cols-4 gap-1.5">
            {preview.phases.map((ph) => {
              const meta = PHASES.find((p) => p.key === ph.phase);
              const positive = ph.dil_delta_g >= 0;
              return (
                <div
                  key={ph.phase}
                  data-testid={`cycle-phase-impact-${ph.phase}`}
                  className={`p-2 rounded-lg text-center border ${
                    (pick === ph.phase || (!pick && derived === ph.phase))
                      ? (isDark ? `border-${meta?.color || "fuchsia"}-500/50 bg-${meta?.color || "fuchsia"}-500/10`
                                : `border-${meta?.color || "fuchsia"}-400 bg-${meta?.color || "fuchsia"}-50`)
                      : (isDark ? "border-white/10 bg-white/[0.02]" : "border-slate-200 bg-slate-50")
                  }`}
                >
                  <div className="text-base leading-none mb-0.5">{meta?.emoji}</div>
                  <div className={`text-[10px] font-bold ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                    {bg ? meta?.labelBg : meta?.labelEn}
                  </div>
                  <div className={`text-xs font-bold mt-1 ${isDark ? "text-white" : "text-slate-900"}`}>
                    {ph.dil_ceiling}
                    <span className="text-[9px] opacity-60 ml-0.5">g</span>
                  </div>
                  <div className={`text-[10px] font-semibold ${
                    positive
                      ? (isDark ? "text-emerald-300" : "text-emerald-600")
                      : (isDark ? "text-rose-300" : "text-rose-600")
                  }`}>
                    {positive ? "+" : ""}{ph.dil_delta_pct}%
                  </div>
                  {ph.hormonal_twin_estimate !== null && ph.hormonal_twin_estimate !== undefined ? (
                    <div className={`text-[9px] mt-0.5 opacity-80 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                      💗 {ph.hormonal_twin_estimate}/100
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
          <p className={`text-[10px] mt-2 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
            {preview.note_bg}
          </p>
        </div>
      ) : null}
      {/* iter1093 — Push notifications toggle for phase changes */}
      {push.supported && (
        <div
          className={`mt-3 flex items-center justify-between gap-2 rounded-xl px-3 py-2 border ${
            push.subscribed
              ? (isDark ? "bg-emerald-500/10 border-emerald-500/30" : "bg-emerald-50 border-emerald-200")
              : (isDark ? "bg-white/[0.03] border-white/10" : "bg-slate-50 border-slate-200")
          }`}
          data-testid="cycle-phase-push-toggle"
        >
          <div className="flex-1 min-w-0">
            <div className={`text-xs font-bold ${
              push.subscribed
                ? (isDark ? "text-emerald-300" : "text-emerald-700")
                : (isDark ? "text-slate-200" : "text-slate-800")
            }`}>
              {bg ? "On-device известия за фаза" : "On-device phase alerts"}
            </div>
            <div className={`text-[11px] ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              {push.subscribed
                ? (bg ? "Активни — ще Ви уведомим при всяка нова фаза." : "Active — you'll get pinged on each transition.")
                : (bg ? "Разрешете нотификации за да получавате обяснения при смяна на фаза." : "Enable to get an explainer on each phase transition.")}
            </div>
          </div>
          <button
            type="button"
            disabled={push.loading}
            onClick={push.subscribed ? push.disable : push.enable}
            data-testid={push.subscribed ? "cycle-phase-push-disable" : "cycle-phase-push-enable"}
            className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold ${
              push.subscribed
                ? (isDark ? "bg-white/10 text-slate-300 hover:bg-white/15" : "bg-white text-slate-700 border border-slate-300 hover:bg-slate-50")
                : (isDark ? "bg-fuchsia-500 hover:bg-fuchsia-400 text-white" : "bg-fuchsia-600 hover:bg-fuchsia-700 text-white")
            }`}
          >
            {push.subscribed ? <BellOff className="w-3.5 h-3.5" /> : <Bell className="w-3.5 h-3.5" />}
            {push.subscribed ? (bg ? "Изключи" : "Off") : (bg ? "Включи" : "Enable")}
          </button>
        </div>
      )}

      {/* iter1094 — Push history timeline */}
      {push.subscribed && pushHistory && (pushHistory.history?.length > 0 || pushHistory.last_pushed_phase) && (
        <div
          className={`mt-3 rounded-xl border p-3 ${
            isDark ? "bg-white/[0.03] border-white/10" : "bg-slate-50 border-slate-200"
          }`}
          data-testid="cycle-phase-push-history"
        >
          <div className={`text-[10px] uppercase font-bold tracking-wider mb-2 ${
            isDark ? "text-slate-400" : "text-slate-500"
          }`}>
            {bg ? "Последни известия" : "Recent alerts"}
          </div>
          {pushHistory.last_pushed_phase ? (
            <div className={`text-xs mb-1 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              {bg ? "Последно: " : "Last: "}
              <strong>{PHASES.find(p => p.key === pushHistory.last_pushed_phase)?.[bg ? "labelBg" : "labelEn"] || pushHistory.last_pushed_phase}</strong>
              {" · "}
              <span className="opacity-70">{formatWhen(pushHistory.last_pushed_at, bg)}</span>
            </div>
          ) : null}
          {pushHistory.history?.length > 1 ? (
            <ul className="space-y-1 mt-2">
              {pushHistory.history.slice().reverse().slice(0, 5).map((h, i) => {
                const meta = PHASES.find(p => p.key === h.phase);
                return (
                  <li
                    key={i}
                    data-testid={`cycle-phase-push-history-item-${i}`}
                    className={`flex items-center gap-2 text-[11px] ${isDark ? "text-slate-400" : "text-slate-600"}`}
                  >
                    <span>{meta?.emoji || "🔔"}</span>
                    <span>{bg ? meta?.labelBg : meta?.labelEn}</span>
                    <span className="opacity-60">· {formatWhen(h.at, bg)}</span>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
      )}
    </div>
  );
}
